import Quill, { Parchment } from 'quill/core';
import React, { useContext, useEffect, useState } from 'react';
import useAppSelector from '../../../app/redux/useAppSelector';
import type { TetherPortalContainer } from '../../../components/Tether';
import { gql } from '../../../graphql/generated/gql';
import type { DocumentType } from '../../../graphql/generated/gql';
import useIsPublicView from '../../../utils/useIsPublicView';
import {
  useQuill,
  useQuillMutations,
  useQuillSelection,
  useQuillState,
} from '../../QuillContext';
import AttachmentMention from '../../formats/AttachmentMention';
import AttachmentMentionToolbar from './AttachmentMentionToolbar';
import EditModeToolbar from './EditModeToolbar';
import ImportedToolbar from './ImportedToolbar';
import LinkToolbar from './LinkToolbar';
import MentionToolbar from './MentionToolbar';
import ReadModeToolbar from './ReadModeToolbar';
import ToolbarPopOver from './ToolbarPopOver';
import type { ToolbarObjectButtonOptions } from './buttons/ToolbarObjectButton';

const IsToolbarDisabledContext = React.createContext(false);

const POST_FRAGMENT = gql(`
  fragment EditorToolbar_post on PostFields {
    id
    ... on Post {
      externalPost {
        id
        ...ImportedToolbar_externalPost
      }
    }
  }
`);

interface BaseEditorToolbarProps {
  readOnly: boolean;
  multiline: boolean;
  inTitleLine: boolean;
  objectButtonOptions?: ToolbarObjectButtonOptions;
  closeToolbar: () => void;
  onLinkSelected: () => void;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseEditorToolbar = ({
  readOnly,
  inTitleLine,
  multiline,
  objectButtonOptions,
  onLinkSelected,
  closeToolbar,
}: BaseEditorToolbarProps) => {
  const { formats } = useQuillSelection();

  const isEditable = useAppSelector((state) => state.editor.editable);
  const isPublicView = useIsPublicView();

  if (readOnly) {
    if (isPublicView && !isEditable) {
      return null;
    }
    return <ReadModeToolbar formats={formats} closeToolbar={closeToolbar} />;
  }
  return (
    <EditModeToolbar
      multiline={multiline}
      inTitleLine={inTitleLine}
      objectButtonOptions={objectButtonOptions}
      closeToolbar={closeToolbar}
      onLinkSelected={onLinkSelected}
    />
  );
};

interface EditorToolbarProps {
  post?: DocumentType<typeof POST_FRAGMENT> | null;
  readOnly: boolean;
  showInTitleLine?: boolean;
  objectButtonOptions?: ToolbarObjectButtonOptions;
  portalContainer?: TetherPortalContainer;
}

const EditorToolbar = ({
  post,
  readOnly,
  showInTitleLine,
  objectButtonOptions,
  portalContainer,
}: EditorToolbarProps) => {
  const { quill } = useQuill();
  const { editingLinkChange } = useQuillMutations();
  const { activeBlot, isEditingLink } = useQuillState();
  const selection = useQuillSelection();
  const { formats } = selection;
  const [isLinkSelected, setIsLinkSelected] = useState(false);

  useEffect(() => {
    setIsLinkSelected(false);
  }, [selection.range]);

  const isToolbarDisabled = useContext(IsToolbarDisabledContext);

  if (isToolbarDisabled || !quill) {
    return null;
  }

  return (
    <ToolbarPopOver
      showInTitleLine={showInTitleLine}
      portalContainer={portalContainer}
    >
      {({ lines, range, isInTitleLine, closeToolbar }) => {
        // LinkToolbar will display when:
        // 1. User is editing the link
        //    (either by creating a new link or clicking the edit icon on LinkToolbar);
        // 2. The selection is a link **and**;
        //    a. The selection is collapsed and is in edit mode,
        //    b. or, User clicked the link icon on EditModeToolbar;
        if (
          isEditingLink ||
          (formats.link && ((!range.length && !readOnly) || isLinkSelected))
        ) {
          return (
            <LinkToolbar
              quill={quill}
              onLinkChange={(value) =>
                quill.format('link', value, Quill.sources.USER)
              }
            />
          );
        }

        if (!range.length) return null;
        if (activeBlot?.statics?.blotName === 'mention')
          // @ts-expect-error Mention is an inline blot. Should be resolved when Quill is migrated to TypeScript
          return <MentionToolbar blot={activeBlot} />;

        if (activeBlot instanceof AttachmentMention) {
          return <AttachmentMentionToolbar blot={activeBlot} />;
        }

        if (lines.every((line) => line instanceof Parchment.EmbedBlot))
          return null;
        if (!quill.getText(range.index, range.length).replace(/\n/g, '').length)
          return null;

        if (post && 'externalPost' in post && post.externalPost) {
          return <ImportedToolbar externalPost={post.externalPost} />;
        }
        return (
          <BaseEditorToolbar
            readOnly={readOnly}
            multiline={lines.length > 1}
            inTitleLine={isInTitleLine}
            objectButtonOptions={objectButtonOptions}
            closeToolbar={closeToolbar}
            onLinkSelected={() => {
              if (!formats.link) {
                editingLinkChange(true);
              }
              setIsLinkSelected(true);
            }}
          />
        );
      }}
    </ToolbarPopOver>
  );
};

EditorToolbar.DisableBoundary = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <IsToolbarDisabledContext.Provider value>
    {children}
  </IsToolbarDisabledContext.Provider>
);

export default EditorToolbar;
